import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { Subscription, Observable, of, BehaviorSubject } from "rxjs";
import { tap, mergeMap, catchError } from "rxjs/operators";
//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { DateService } from "../../../common/services/date.service";
import { RouterLinkRendererComponent } from "../../../common/components/ag-grid/router-link-renderer.component";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { ProgramAreaGroupMap, IssuesStatus, Issue, IssueHistory, IssueNote, IssueSteps, IssueEscalationMethods, IssueNotice, IssueNoticeType, IssueReassignment, ContestIssue, IssueStatusChange, IssueExtension, IssueEscalation, IssueEscalationDecision, EscalationStatus, UpdateIssueRequest } from "../../../common/models/issue.model";
import { OrganizationService } from "../../../common/services/organization.service";
import { OrganizationStub } from "../../../common/models/organization.model";
import { Option } from "../../../common/models/flow.dto";
import { ContactService, ContactSearchSpec } from "../../../admin/services/contacts.service";
import { ContactStub, Contact } from "../../../admin/models/contact.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { LoaderService } from "../../../common/services/loader.service";
import { WellService } from "../../../common/services/well.service";
import { WellStub, GISPoint, GISPointTypes, DataViewRequest } from '../../../common/models/well.model';
import { SidePanelService } from "../../../common/services/side-panel.service";
import { SidePanel } from "../../../common/models/side-panel.model";
import { AuthService } from "../../../services/auth.service";
import { LunaClaimValues, LunaClaimTypes } from "../../../models/user-identity.model";

import { IssuesService } from "../../../common/services/issues.service";

@Component({
    selector: 'review-issue',
    templateUrl: './review-issue.component.html',
    styleUrls: ['./review-issue.component.scss'],
    standalone: false
})
export class ReviewIssueComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    affectedWells: Observable<any[]>;
    assignedDialog: Dialog = new Dialog("Issue [XXX] has been assigned to [User].", [new DialogButton("OK", true, () => { this.complete(); })]);
    assignedTo: ContactStub;

    confirmDeactivate: Dialog = new Dialog("You have unsubmitted changes.  Are you sure you wish to leave the page?", [new DialogButton("Cancel", false, () => { return false; }), new DialogButton("OK", true, () => { this.allowDeactivate.next(true); })]);
    updateSuccessDialog: Dialog = new Dialog("Issue [XXX] has been updated.", [new DialogButton("OK", true, () => { this.complete(); })]);
    organizationNotFoundDialog: Dialog = new Dialog("", [new DialogButton("OK", true, () => { return; })]);
    organizationNotFoundMessage: string = "Unable to location organization [XXX].";


    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    get dateContestedInvalid(): boolean {
        let invalid: boolean = false;
        if (this.form != null) {
            const workingGroup = this.form.get("keepWorkingForm");
            if (workingGroup != null) {
                const contestedGroup = workingGroup.get("dateContested");
                if (contestedGroup != null) {
                    const pickerCtrl = contestedGroup.get("datePicker");
                    invalid = pickerCtrl.invalid;
                }
            }
        }

        return invalid;
    }

    get dateNextInvalid(): boolean {
        let invalid: boolean = false;
        if (this.form != null) {
            const dateNextGroup = this.form.get("dateOfNextAction");
            if (dateNextGroup != null) {
                const pickerCtrl = dateNextGroup.get("datePicker");
                invalid = pickerCtrl.invalid;
            }
        }
        return invalid;
    }

    get extensionDateInvalid(): boolean {
        let invalid: boolean = false;
        if (this.form != null) {
            const workingGroup = this.form.get("keepWorkingForm");
            if (workingGroup != null) {
                const expirationGroup = workingGroup.get("extensionExpiration");
                if (expirationGroup != null) {
                    const pickerCtrl = expirationGroup.get("datePicker");
                    invalid = pickerCtrl.invalid;
                }
            }
        }
        return invalid;
    }

    get historicOwnerNumber(): string {
        return (this.organization != null)
            ? this.organization.historicalOwnerNumber
            : this.issue != null
                ? this.issue.legacyId
                : "";
    }

    get organizationId(): string {
        return (this.organization != null)
            ? this.organization.id
            : this.issue != null
                ? this.issue.organizationId
                : "";
    }

    get organizationName(): string {
        return (this.organization != null)
            ? this.organization.name
            : this.issue != null
                ? this.issue.organizationNameAtTimeOfIssue
                : "";
    }

    get previousDecision(): string {
        if (this.issue == null) return "";
        if (this.issue.escalationStatus !== EscalationStatus.Approved && this.issue.escalationStatus !== EscalationStatus.Rejected) return "";
        return this.issue.escalationStatus;
    }

    get resolutionDateInvalid(): boolean {
        let invalid: boolean = false;
        if (this.form != null) {
            const resolvedForm = this.form.get("resolvedForm");
            if (resolvedForm != null) {
                const resolutionDate = resolvedForm.get("resolvedDate");
                if (resolutionDate != null) {
                    const pickerCtrl = resolutionDate.get("datePicker");
                    invalid = pickerCtrl.invalid;
                }
            }
        }
        return invalid;
    }

    get escalationForm(): FormGroup {
        if (this.form == null) return new FormGroup({});
        let escForm = this.form.get("escalationForm");
        if (escForm == null) return new FormGroup({});
        return <FormGroup>escForm;
    }

    escalationMethods = IssueEscalationMethods;

    get escalationOptions(): Option[] {
        return [
            { id: "", text: "-- Select --" } as Option,
            { id: IssueEscalationMethods.ChiefsOrder, text: IssueEscalationMethods.ChiefsOrder } as Option,
            { id: IssueEscalationMethods.ComplianceNotice, text: IssueEscalationMethods.ComplianceNotice } as Option,
            { id: IssueEscalationMethods.AttorneyGeneral, text: IssueEscalationMethods.AttorneyGeneral } as Option,
            { id: IssueEscalationMethods.ComplianceAgreement, text: IssueEscalationMethods.ComplianceAgreement } as Option
        ] as Option[];
    }

    get extensionGranted(): string {
        let granted: string = "No";
        if (this.issue != null && this.issue.issueExtensions != null) {
            granted = (this.issue.issueExtensions.length > 0) ? "Yes" : "No";
        }
        return granted;
    }

    programAreaGroupMap: ProgramAreaGroupMap[] = [];

    get isContested(): boolean {
        if (this.form == null) return false;
        let escalationForm = this.form.get('escalationForm');
        if (escalationForm == null) return false;
        let contestedField = escalationForm.get('contested');
        return contestedField != null && contestedField.value === 'Yes';
    }

    get isEditable(): boolean {
        let editable: boolean = false;
        if (this.auth.User.hasPermission(LunaClaimTypes.System, LunaClaimValues.ApproveIssueEscalation)) editable = true;
        if (editable === false) {
            const mappedGroup = this.programAreaGroupMap.find(m => m.programArea === this.issue.issueOwner);
            if (mappedGroup != null) {
                editable = this.auth.User.groups.find(g => g === mappedGroup.groupId) != null;
            }
        }
        return editable;
    }

    form: FormGroup;

    get isApproveForm(): boolean {
        if (this.route == null || this.route.snapshot == null || this.route.snapshot.url == null) return false;
        return this.route.snapshot.url.length > 0 && this.route.snapshot.url[0].path === "approve-escalation";
    }

    issue: Issue;
    issueId: string;

    get keepWorkingForm(): FormGroup {
        let workingForm: FormGroup = new FormGroup({});
        if (this.form != null) workingForm = <FormGroup>this.form.get('keepWorkingForm');
        return workingForm;
    }

    lastInspector: ContactStub;

    get preletter(): IssueNotice {
        if (this.issue == null || this.issue.issueNotices == null) return null;
        return this.issue.issueNotices.find(n => n.noticeType === IssueNoticeType.PreLetter);
    }

    get letter1(): IssueNotice {
        if (this.issue == null || this.issue.issueNotices == null) return null;
        return this.issue.issueNotices.find(n => n.noticeType === IssueNoticeType.Letter1);
    }

    get letter2(): IssueNotice {
        if (this.issue == null || this.issue.issueNotices == null) return null;
        return this.issue.issueNotices.find(n => n.noticeType === IssueNoticeType.Letter2);
    }

    get complianceNotice1(): IssueNotice {
        if (this.issue == null || this.issue.issueNotices == null) return null;
        return this.issue.issueNotices.find(n => n.noticeType === IssueNoticeType.ComplianceNotice1);
    }

    get complianceNotice2(): IssueNotice {
        if (this.issue == null || this.issue.issueNotices == null) return null;
        return this.issue.issueNotices.find(n => n.noticeType === IssueNoticeType.ComplianceNotice2);
    }

    get nextStepValue(): string {
        if (this.form == null) return "";
        const ctrl = this.form.get('nextStep');
        if (ctrl == null || ctrl.value == null) return "";
        return ctrl.value.toString();
    }

    organization: OrganizationStub;

    get organizationRegistered(): string {
        if (this.organization == null) return "No";
        return (this.organization.hasRegistered) ? "Yes" : "No";
    }
    steps = IssueSteps;
    statuses = IssuesStatus;

    get statusList(): Option[] {
        return [
            { id: IssuesStatus.Pending, text: IssuesStatus.Pending } as Option,
            { id: IssuesStatus.Validated, text: IssuesStatus.Validated } as Option,
            { id: IssuesStatus.Escalated, text: IssuesStatus.Escalated } as Option,
            { id: IssuesStatus.ChiefsOrderApproval, text: IssuesStatus.ChiefsOrderApproval } as Option,
            { id: IssuesStatus.Contested, text: IssuesStatus.Contested } as Option,
            { id: IssuesStatus.Closed, text: IssuesStatus.Closed } as Option
        ] as Option[];
    }

    get stepList(): Option[] {
        return [
            { id: "", text: "-- Select --" } as Option,
            { id: IssueSteps.KeepWorking, text: IssueSteps.KeepWorking } as Option,
            { id: IssueSteps.PreLetter, text: IssueSteps.PreLetter } as Option,
            { id: IssueSteps.Letter1, text: IssueSteps.Letter1 } as Option,
            { id: IssueSteps.Letter2, text: IssueSteps.Letter2 } as Option,
            { id: IssueSteps.ComplianceNotice1, text: IssueSteps.ComplianceNotice1 } as Option,
            { id: IssueSteps.ComplianceNotice2, text: IssueSteps.ComplianceNotice2 } as Option,
            { id: IssueSteps.Close, text: IssueSteps.Close } as Option,
            { id: IssueSteps.Escalate, text: IssueSteps.Escalate } as Option
        ] as Option[];
    }
    get yesNoOptions(): Option[] {
        return [
            { id: "No", text: "No" } as Option,
            { id: "Yes", text: "Yes" } as Option
        ] as Option[];
    }

    subscription: Subscription = new Subscription();

    timelinePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Timeline" }],
        sections: [{ componentName: "IssueTimeline" }]
    });

	gridOptions: GridOptions;
	gridApi?: GridApi;


    wellColumns: ColDef[] = [
        {
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                openInNewWindow: true,
                textField: "id",
                url: (data: WellStub) => { return `/common/organization/${data.organizationId}/well/${data.id}` }
            },
            headerName: "API Number",
            headerTooltip: "API Number",
            field: "id",
            tooltipField: "id",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 130
        } as ColDef,
        {
            headerName: "Well Name",
            headerTooltip: "Well Name",
            field: "wellName",
            tooltipField: "wellName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef,
        {
            headerName: "Well Number",
            headerTooltip: "Well Number",
            field: "wellNumber",
            tooltipField: "wellNumber",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 140
        } as ColDef,
        {
            headerName: "County",
            headerTooltip: "County",
            field: "county",
            tooltipField: "county",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 135
        } as ColDef,
        {
            headerName: "Township",
            headerTooltip: "Township",
            field: "township",
            tooltipField: "township",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150
        } as ColDef
    ];

    get doApproveRedirect(): boolean {
        if (this.issue == null) return false;
        return this.issue.status === IssuesStatus.ChiefsOrderApproval && (this.issue.escalationStatus == null || this.issue.escalationStatus === EscalationStatus.AwaitingDecision) && !this.isApproveForm;
    }

    get doReviewRedirect(): boolean {
        if (this.issue == null) return false;
        return this.issue.status !== IssuesStatus.ChiefsOrderApproval && this.isApproveForm;
    }

    constructor(dialogService: DynamicDialogService, private builder: FormBuilder, private router: Router, private route: ActivatedRoute, private headerService: SubHeaderService, private formatter: GridFormatterService, auth: AuthService,
            private issuesService: IssuesService, private organizationService: OrganizationService, private contactService: ContactService, private wellService: WellService, private loader: LoaderService, private dateService: DateService, private sidePanelService: SidePanelService ) {
        super(dialogService, auth);
    }

    ngOnInit() {
        this.confirmDialog = this.confirmDeactivate;
        this.allowDeactivate.next(true);
        this.issueId = this.route.snapshot.params.id;
        this.headerService.updateSubHeader({ title: "Review Issue"});
        this.loader.toggleLoader(true);
        const mapSubs$ = this.issuesService.getGroupMaps().pipe()
            .subscribe(
                (map: ProgramAreaGroupMap[]) => { this.programAreaGroupMap = map; },
                (error) => { this.handleError(error); }
        );
        this.subscription.add(mapSubs$);
        const issueSubs$ = this.issuesService.getIssueById(this.issueId).pipe(
            tap((issue: Issue) => {
                this.issue = issue;
                this.headerService.updateSubHeader({ title: `Review Issue - ${this.issue.identifier}`, buttons: [{ name: "View History", method: () => this.viewSidePanel() }] });  
                if (this.issue.issueHistories == null || this.issue.issueHistories.length === 0) {
                    this.issue.issueHistories = [{ id: "", issueId: this.issue.id, eventDate: this.issue.creationDate, description: "Issue Created", changedByUserId: "", changedByUserName: "System" } as IssueHistory ];
                }
                this.timelinePanel.sections[0].componentData = this.issue.issueHistories;
                if (this.issue.wellId != null && this.issue.wellId.length > 0) {
                    let obs$: Observable<any> = of([]);
                    if (this.issue.wellId != null && this.issue.wellId.length > 0) {
                        obs$ = this.wellService.getTargetedWellData([this.issue.wellId], DataViewRequest.ReviewIssue);
                    }
                    this.affectedWells = obs$.pipe(
                        tap((wells: any[]) => { setTimeout(() => { if (this.gridApi) this.gridApi.sizeColumnsToFit(); }, 500) })
                    );
                    this.buildGridOptions();
                }
            }),
            mergeMap((issue: Issue) => {
                if (issue == null || issue.organizationId == null) return of(null);
                if (this.doApproveRedirect) return of(null);
                let searchOrganizationId: string = (issue.currentRICSOrganizationId == null || issue.currentRICSOrganizationId.length === 0) ? issue.organizationId : issue.currentRICSOrganizationId;
                const obs$: Observable<OrganizationStub> =
                    (searchOrganizationId.startsWith("ON"))
                        ? this.organizationService.getOrganizationStub(searchOrganizationId)
                        : this.organizationService.getOrganizationStubByHistoricalOwnerNumber(searchOrganizationId);
                return obs$.pipe(
                    catchError((err) => {
                        console.log('error in organization load', err);
                        return of(null);
                    }),
                    tap((org: OrganizationStub) => {
                        this.organization = org;
                }));
            }),
            mergeMap((organization: OrganizationStub) => {
                if (this.issue == null) return of(null);
                if (this.doApproveRedirect) return of(null);
                const contactsToLoad: string[] = [];
                if (this.issue.inspectorId != null && this.issue.inspectorId.length > 0) contactsToLoad.push(this.issue.inspectorId);
                if (this.issue.assignee != null && this.issue.assignee.length > 0) contactsToLoad.push(this.issue.assignee);
                if (contactsToLoad.length === 0) return of([]);
                const spec = {
                    ids: contactsToLoad,
                    stubs: true
                } as ContactSearchSpec;                
                return this.contactService.getAll(spec).pipe(tap((contacts: Contact[]) => {
                    this.lastInspector = contacts.find(c => c.id === this.issue.inspectorId);
                    this.assignedTo = contacts.find(c => c.id === this.issue.assignee);
                }));
            })
        ).subscribe(
            () => {
                if (this.doApproveRedirect) this.activateApproveForm()
                else if (this.doReviewRedirect) this.activateReviewForm();
                else {
                    this.loader.toggleLoader(false);
                    this.form = this.buildForm();
                    this.dataLoading.next(false);
                }               
            },
            (error) => {
                this.handleError(error);
            }
        );
        this.subscription.add(issueSubs$);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription) this.subscription.unsubscribe();
    }

    activateApproveForm(): void {
        this.router.navigate([`/issues/approve-escalation/${this.issue.id}`]);
    }

    activateReviewForm(): void {
        this.router.navigate([`/issues/review-issue/${this.issue.id}`]);
    }

    addNote(note: IssueNote): void {
        if (this.issue.issueNotes == null) {
            this.issue.issueNotes = [];
        }
        this.issue.issueNotes.push(note);
        this.allowDeactivate.next(false);
    }

    assignReviewer(user: ContactStub): void {
        this.assignedTo = user;        
        if (this.allowDeactivate.value === true) { //means no other values on the page ought to have been changed, so update the user and exit immediately
            this.loader.toggleLoader(true);    
            const assignObs$ = this.issuesService.assignIssue(this.issue.id, { currentUser: this.issue.assignee, newUser: this.assignedTo.id } as IssueReassignment).pipe()
                .subscribe(
                    (issue: Issue) => {
                        this.assignedDialog.message = this.assignedDialog.message.replace('[XXX]', this.issue.identifier);
                        this.assignedDialog.message = this.assignedDialog.message.replace('[User]', user.displayName);
                        this.assignedDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.assignedDialog);
                    },
                    (error) => { this.handleError(error); }
                );
            this.subscription.add(assignObs$);
        }
    }


    buildEscalationForm(): FormGroup {
        const contested = (this.issue.wasIssueContested) ? "Yes" : "No";
        let chiefsOrderIssued: any = null;
        if (this.issue.chiefsOrderId != null && this.issue.chiefsOrderId.length > 0) {
            chiefsOrderIssued = new Date(this.issue.issueChiefsOrderTracking.chiefsOrderCreationDate);
        }
        const referralDate = this.issue.agreferralDate != null ? new Date(this.issue.agreferralDate) : null;
        const agreementDate = this.issue.complianceAgreementDate != null ? new Date(this.issue.complianceAgreementDate) : null;
        const noticeDate = this.issue.complianceNoticeIssued != null ? new Date(this.issue.complianceNoticeIssued) : null;
        const noticeDueDate = this.issue.complianceNoticeDueDate != null ? new Date(this.issue.complianceNoticeDueDate) : null;
        const agreementDueDate = this.issue.complianceAgreementDueDate != null ? new Date(this.issue.complianceAgreementDueDate) : null;
        let method: string | null = this.issue.escalationStatus;
        if (this.isApproveForm || (method != null && this.escalationOptions.find(m => m.id === method) == null)) {
            method = IssueEscalationMethods.ChiefsOrder;
        } 
        let pendingForm = this.builder.group({
            contested: [{ value: contested, disabled: !this.isEditable }, []],
            contestedReason: [{ value: this.issue.contestedReason, disabled: !this.isEditable }, []],
            escalationMethod: [{ value: method, disabled: !this.isEditable }, []],
            chiefsOrderNumber: [{ value: this.issue.chiefsOrderId, disabled: !this.isEditable }, []],
            dateChiefsOrderIssued: this.builder.group({ datePicker: [{ value: chiefsOrderIssued, disabled: !this.isEditable }, []] }),
            complianceNoticeNumber: [{ value: this.issue.complianceNoticeNumber, disabled: !this.isEditable }, []],
            complianceNoticeIssued: this.builder.group({ datePicker: [{ value: noticeDate, disabled: !this.isEditable }, []] }),
            complianceNoticeDueDate: this.builder.group({ datePicker: [{ value: noticeDueDate, disabled: !this.isEditable }]}),
            referralNumber: [{ value: this.issue.agreferralNumber, disabled: !this.isEditable }, []],
            referralDate: this.builder.group({ datePicker: [{ value: referralDate, disabled: !this.isEditable }, []] }),
            complianceAgreementNumber: [{ value: this.issue.complianceAgreementNumber, disabled: !this.isEditable }, []],
            complianceAgreementIssued: this.builder.group({ datePicker: [{ value: agreementDate, disabled: !this.isEditable }, []] }),
            complianceAgreementDueDate: this.builder.group({ datePicker: [{ value: agreementDueDate, disabled: !this.isEditable }, []] }),
        });
        if (method === IssueEscalationMethods.ComplianceNotice) {
            pendingForm.get("complianceNoticeDueDate")?.get("datePicker")?.setValidators(Validators.required);
        }
        if (method === IssueEscalationMethods.ComplianceAgreement) {
            pendingForm.get("complianceAgreementDueDate")?.get("datePicker")?.setValidators(Validators.required);
        }
        return pendingForm;
    }

    buildLetterForm(): FormGroup {
        let preletterMailed = (this.preletter != null && this.preletter.dateSent != null) ? new Date(this.preletter.dateSent) : "";
        let preletterReceived = (this.preletter != null && this.preletter.dateReceived != null) ? new Date(this.preletter.dateReceived) : "";
        let letter1Mailed = (this.letter1 != null && this.letter1.dateSent != null) ? new Date(this.letter1.dateSent) : "";
        let letter1Received = (this.letter1 != null && this.letter1.dateReceived != null) ? new Date(this.letter1.dateReceived) : "";
        let letter1DueDate = (this.letter1 != null && this.letter1.dateDue != null) ? new Date(this.letter1.dateDue) : "";
        let letter1ExpirationDate = (this.letter1 != null && this.letter1.expirationDate != null) ? new Date(this.letter1.expirationDate) : "";
        let letter2Mailed = (this.letter2 != null && this.letter2.dateSent != null) ? new Date(this.letter2.dateSent) : "";
        let letter2Received = (this.letter2 != null && this.letter2.dateReceived != null) ? new Date(this.letter2.dateReceived) : "";
        let complianceNotice1Mailed = (this.complianceNotice1 != null && this.complianceNotice1.dateSent != null) ? new Date(this.complianceNotice1.dateSent) : "";
        let complianceNotice1Received = (this.complianceNotice1 != null && this.complianceNotice1.dateReceived != null) ? new Date(this.complianceNotice1.dateReceived) : "";
        let complianceNotice1Expiration = (this.complianceNotice1 != null && this.complianceNotice1.expirationDate != null) ? new Date(this.complianceNotice1.expirationDate) : "";
        let complianceNotice2Mailed = (this.complianceNotice2 != null && this.complianceNotice2.dateSent != null) ? new Date(this.complianceNotice2.dateSent) : "";
        let complianceNotice2Received = (this.complianceNotice2 != null && this.complianceNotice2.dateReceived != null) ? new Date(this.complianceNotice2.dateReceived) : "";

        return this.builder.group({
            preletter: [{ value: (this.preletter != null) ? this.preletter.document : "", disabled: !this.isEditable }, []],
            preletterMailed: this.builder.group({ datePicker: [{ value: preletterMailed, disabled: !this.isEditable }, []] }),
            preletterReceived: this.builder.group({ datePicker: [{ value: preletterReceived, disabled: !this.isEditable }, []] }),
            letter1: [{ value: (this.letter1 != null) ? this.letter1.document : "", disabled: !this.isEditable }, []],
            letter1Mailed: this.builder.group({ datePicker: [{ value: letter1Mailed, disabled: !this.isEditable }, []] }),
            letter1Received: this.builder.group({ datePicker: [{ value: letter1Received, disabled: !this.isEditable }, []] }),
            letter1DueDate: this.builder.group({ datePicker: [{ value: letter1DueDate, disabled: !this.isEditable }, []] }),
            letter1ExpirationDate: this.builder.group({ datePicker: [{ value: letter1ExpirationDate, disabled: !this.isEditable }, []] }),
            letter2: [{ value: (this.letter2 != null) ? this.letter2.document : "", disabled: !this.isEditable }, []],
            letter2Mailed: this.builder.group({ datePicker: [{ value: letter2Mailed, disabled: !this.isEditable }, []] }),
            letter2Received: this.builder.group({ datePicker: [{ value: letter2Received, disabled: !this.isEditable }, []] }),
            complianceNotice1: [{ value: (this.complianceNotice1 != null) ? this.complianceNotice1.document : "", disabled: !this.isEditable }, []],
            complianceNotice1Mailed: this.builder.group({ datePicker: [{ value: complianceNotice1Mailed, disabled: !this.isEditable }, []] }),
            complianceNotice1Received: this.builder.group({ datePicker: [{ value: complianceNotice1Received, disabled: !this.isEditable }, []] }),
            complianceNotice1ExpirationDate: this.builder.group({ datePicker: [{ value: complianceNotice1Expiration, disabled: !this.isEditable }, []] }),
            complianceNotice2: [{ value: (this.complianceNotice2 != null) ? this.complianceNotice2.document : "", disabled: !this.isEditable }, []],
            complianceNotice2Mailed: this.builder.group({ datePicker: [{ value: complianceNotice2Mailed, disabled: !this.isEditable }, []] }),
            complianceNotice2Received: this.builder.group({ datePicker: [{ value: complianceNotice2Received, disabled: !this.isEditable }, []] }),
        });
    }


    buildForm(): FormGroup {
        const nextActionDate = (this.issue.dateOfNextAction != null) ? new Date(this.issue.dateOfNextAction) : null;
        const nextStep = this.isApproveForm ? IssueSteps.Escalate : (this.issue.nextStep != null) ? this.issue.nextStep : "";
        return this.builder.group({
            status: [{ value: this.issue.status, disabled: !this.isEditable }, [Validators.required]],
            dateOfNextAction: this.builder.group({ datePicker: [{ value: nextActionDate, disabled: !this.isEditable }, []] }),
            preLetterSent: [{ value: (this.preletter != null && this.preletter.dateSent != null), disabled: true }, []],
            letter1Sent: [{ value: (this.letter1 != null && this.letter1.dateSent != null), disabled: true }, []],
            letter2Sent: [{ value: (this.letter2 != null && this.letter2.dateSent != null), disabled: true }, []],
            nextStep: [{ value: nextStep, disabled: !this.isEditable }, [Validators.required]],
            keepWorkingForm: this.buildKeepWorkingForm(),
            resolvedForm: this.buildResolvedForm(),
            escalationForm: this.buildEscalationForm(),
            lettersForm: this.buildLetterForm()
        });
    }

    buildGridOptions(): void {
		this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.wellColumns, false, true, true, true);
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
    }

    buildKeepWorkingForm(): FormGroup
    {
        const dateDue = (this.issue.dateDue != null) ? new Date(this.issue.dateDue) : null;
        const isContested = this.issue.wasIssueContested;
        let contestString = (isContested) ? "Yes" : "No";
        const contestedDate = (this.issue.contestedDate != null) ? new Date(this.issue.contestedDate) : null;

        return this.builder.group({
            extensionRequested: [{ value: "No", disabled: !this.isEditable }, []],
            extensionGranted: [{ value: false, disabled: !this.isEditable }, []],
            extensionExpiration: this.builder.group({ datePicker: [{ value: dateDue, disabled: !this.isEditable }, []] }),
            contested: [{ value: contestString, disabled: !this.isEditable }, []],
            dateContested: this.builder.group({ datePicker: [{ value: contestedDate, disabled: !this.isEditable }, []] }),
            contestedReason: new FormGroup({})
        });
    }

    buildResolvedForm(): FormGroup {
        const resolvedDate = (this.issue.resolutionDate != null) ? new Date(this.issue.resolutionDate) : null;
        return this.builder.group({
            resolvedDate: this.builder.group({ datePicker: [{ value: resolvedDate, disabled: !this.isEditable }, []] })
        });
    }

    cancel(): void {
        this.router.navigate(["/home"]);
    }

    complete(): void {
        this.form.reset();
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    contestedChanged(evt: any): void {
        const workingGroup = <FormGroup>this.form.get("keepWorkingForm");
        if (workingGroup != null) {
            const contestedCtrl = workingGroup.get("contested");
            if (contestedCtrl != null) {
                const dateContestedGroup = workingGroup.get("dateContested");
                if (dateContestedGroup != null) {
                    const pickerCtrl = dateContestedGroup.get("datePicker");
                    if (pickerCtrl != null) {
                        if (contestedCtrl.value === 'Yes') {
                            pickerCtrl.setValidators([Validators.required]);
                        } else {
                            pickerCtrl.clearValidators();
                            const contestedReason = workingGroup.get("contestedReason");
                            if (contestedReason != null) {
                                workingGroup.removeControl("contestedReason");
                            }
                            workingGroup.addControl("contestedReason", this.builder.group({}));
                        }
                        pickerCtrl.updateValueAndValidity();
                    }
                }
            }
        }
    }

    contestedFormBuilt(builtForm: FormGroup): void {
        const workingGroup = <FormGroup>this.form.get("keepWorkingForm");
        if (workingGroup != null) {
            let contestedReason = workingGroup.get("contestedReason");
            if (contestedReason != null) {
                workingGroup.removeControl("contestedReason");
            }
            const contentCtrl = builtForm.get("content");
            if (contentCtrl != null && this.issue.contestedReason != null) {
                contentCtrl.setValue(this.issue.contestedReason);
            }
            workingGroup.addControl("contestedReason", builtForm);
        }
    }



    decide(approve: boolean): void {
        const decision = { approved: approve, issueId: this.issue.id, ownerName: this.organizationName } as IssueEscalationDecision;
        this.loader.toggleLoader(true);
        const save$ = this.issuesService.decisionIssueEscalation(decision).pipe(
            mergeMap((issue: Issue) => {
                this.issue = issue;
                return this.saveIssue();
            })
        ).subscribe(
            (issue: Issue) => {
                this.loader.toggleLoader(false);
                this.displayResult();
            },
            (error) => { this.handleError(error); }
        );
        this.subscription.add(save$);
    }

    deleteNote(index: number): void {
        if (this.issue.issueNotes == null || index >= this.issue.issueNotes.length) return;
        if (index > -1) {
            this.issue.issueNotes.splice(index, 1);
        }
    }

    displayResult(): void {
        this.updateSuccessDialog.message = this.updateSuccessDialog.message.replace('[XXX]', this.issue.identifier);
        this.updateSuccessDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.updateSuccessDialog);
    }

    escalationMethodChanged(evt: any): void {
        let methodCtrl = this.escalationForm.get("escalationMethod");
        if (methodCtrl != null) {
            let method = methodCtrl.value;
            let noticeDueDateCtrl = this.escalationForm.get("complianceNoticeDueDate").get("datePicker");
            let agreementDueDateCtrl = this.escalationForm.get("complianceAgreementDueDate").get("datePicker");
            if (method === IssueEscalationMethods.ComplianceNotice) {
                noticeDueDateCtrl.setValidators(Validators.required);
                agreementDueDateCtrl.clearValidators();
            } else if (method === IssueEscalationMethods.ComplianceAgreement) {
                noticeDueDateCtrl.clearValidators();
                agreementDueDateCtrl.setValidators(Validators.required);
            } else {
                noticeDueDateCtrl.clearValidators();
                agreementDueDateCtrl.clearValidators();
            }
            noticeDueDateCtrl.updateValueAndValidity();
            agreementDueDateCtrl.updateValueAndValidity();
        }
    }

    extensionGrantedChanged(evt: any): void {
        const workingForm = this.form.get("keepWorkingForm");
        if (workingForm != null) {
            const grantedCtrl = workingForm.get("extensionGranted");
            const expirationGroup = workingForm.get("extensionExpiration");
            if (grantedCtrl != null && expirationGroup != null) {
                const picker = expirationGroup.get("datePicker");
                if (<boolean>grantedCtrl.value === true) {
                    picker.setValidators([Validators.required]);
                } else {
                    picker.clearValidators();
                }
                picker.updateValueAndValidity();
            }
        }
    }

    getCountyName(array: GISPoint[]) {
        let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
        return pt && pt.county ? pt.county.name : '';
    }

    getTownshipName(array: GISPoint[]) {
        let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
        return pt && pt.township ? pt.township.name : '';
    }

    handleError(err: any): void {
        this.loader.toggleLoader(false);
        console.error(err);
        this.dialogService.showError();
    }

    nextActionDateChanged(date: Date): void {
        //leaving in, not sure this event monitoring is necessary given how the form group is set up
    }

    nextStepChanged(step: any): void {
        const stepCtrl = this.form.get("nextStep");
        if (stepCtrl != null) {
            this.allowDeactivate.next(false);
            const next = stepCtrl.value;
            const actionGroup = this.form.get("dateOfNextAction");
            const keepWorkingGroup = this.form.get("keepWorkingForm");
            const resolvedGroup = this.form.get("resolvedForm");
            const escalationGroup = this.form.get("escalationForm");
            const letterGroup = this.form.get("lettersForm");
            let actionCtrl: FormControl | null = null;
            if (actionGroup != null) {
                actionCtrl = <FormControl>actionGroup.get("datePicker");
            }
            switch (next) {
                case IssueSteps.KeepWorking:
                    if (actionCtrl != null) {
                        actionCtrl.setValidators([Validators.required]);
                        actionCtrl.updateValueAndValidity();
                    }
                    if (resolvedGroup != null) {
                        this.form.removeControl("resolvedForm");
                    }
                    this.form.addControl("resolvedForm", this.buildResolvedForm());
                    if (escalationGroup != null) {
                        this.form.removeControl("escalationForm");
                    }
                    this.form.addControl("escalationForm", this.buildEscalationForm());
                    if (letterGroup != null) {
                        this.form.removeControl("lettersForm");
                    }
                    this.form.addControl("lettersForm", this.buildLetterForm());
                    break;
                case IssueSteps.Close: {
                    if (actionCtrl != null) {
                        actionCtrl.clearValidators();
                    }
                    if (keepWorkingGroup != null) {
                        this.form.removeControl("keepWorkingForm");
                    }
                    const rebuiltForm = this.buildKeepWorkingForm();
                    this.form.addControl("keepWorkingForm", rebuiltForm);
                    if (resolvedGroup != null) {
                        const resolvedDate = <FormGroup>resolvedGroup.get("resolvedDate");
                        if (resolvedDate != null) {
                            const pickerCtrl = resolvedDate.get("datePicker");
                            pickerCtrl.setValidators([Validators.required]);
                        }
                    }
                    if (escalationGroup != null) {
                        this.form.removeControl("escalationForm");
                    }
                    this.form.addControl("escalationForm", this.buildEscalationForm());
                    if (letterGroup != null) {
                        this.form.removeControl("lettersForm");
                    }
                    this.form.addControl("lettersForm", this.buildLetterForm());
                    break;
                }
                case IssueSteps.Escalate: {
                    if (keepWorkingGroup != null) {
                        this.form.removeControl("keepWorkingForm");                        
                    }
                    this.form.addControl("keepWorkingForm", this.buildKeepWorkingForm());
                    if (resolvedGroup != null) {
                        this.form.removeControl("resolvedForm");
                    }
                    this.form.addControl("resolvedForm", this.buildResolvedForm());
                    if (letterGroup != null) {
                        this.form.removeControl("lettersForm");
                    }
                    this.form.addControl("lettersForm", this.buildLetterForm());
                    const methodCtrl = escalationGroup.get("escalationMethod");
                    if (methodCtrl != null) {
                        methodCtrl.setValidators([Validators.required]);
                    }
                    break;
                }
                case IssueSteps.PreLetter:
                case IssueSteps.Letter1:
                case IssueSteps.Letter2:
                case IssueSteps.ComplianceNotice1:
                case IssueSteps.ComplianceNotice2: {
                    if (keepWorkingGroup != null) {
                        this.form.removeControl("keepWorkingForm");
                    }
                    this.form.addControl("keepWorkingForm", this.buildKeepWorkingForm());
                    if (resolvedGroup != null) {
                        this.form.removeControl("resolvedForm");
                    }
                    this.form.addControl("resolvedForm", this.buildResolvedForm());
                    if (escalationGroup != null) {
                        this.form.removeControl("escalationForm");
                    }
                    this.form.addControl("escalationForm", this.buildEscalationForm());
                    break;
                }
                default: {

                }
            }
        }
        this.form.updateValueAndValidity();
    }

    save(): void {
        if (this.nextStepValue === IssueSteps.Escalate) {
            let actionForm: FormGroup = <FormGroup>this.form.get("escalationForm");
            if (actionForm != null) {
                const methodCtrl = actionForm.get("escalationMethod");
                if (methodCtrl != null) {
                    const method = <IssueEscalationMethods>methodCtrl.value;
                    if (method === IssueEscalationMethods.ChiefsOrder && this.issue.escalationStatus !== EscalationStatus.Approved) {
                        this.issue.escalationStatus = EscalationStatus.AwaitingDecision; //reset the chief's order decision, as its assumed if its being submitted from here with that type, its either a new request or a re-submit of a previously declined one
                    }
                }
            }
        }

        const saveObs$ = this.saveIssue().pipe()
            .subscribe(
                (issue: Issue) => {
                    this.loader.toggleLoader(false);
                    this.displayResult();
                },
                (error) => { this.handleError(error); }
            );
        this.subscription.add(saveObs$);
    }

    saveIssue(): Observable<Issue> {
        let status: IssuesStatus;
        switch (this.nextStepValue) {
            case IssueSteps.Close:
                status = IssuesStatus.Closed;
                break;
            case IssueSteps.Escalate:
                status = IssuesStatus.Escalated;
                break;
            default:
                status = (this.issue.status == null || this.issue.status == IssuesStatus.Pending) ? IssuesStatus.Validated : this.issue.status;
                break;
            
        }
        const updateRequest = new UpdateIssueRequest(this.issue, status);
        updateRequest.currentStep = this.nextStepValue;
        let actionForm: FormGroup;
        switch (this.nextStepValue) {
            case IssueSteps.Close: {
                actionForm = <FormGroup>this.form.get("resolvedForm");
                if (actionForm != null) {
                    const dateCtrl = actionForm.get("resolvedDate");
                    if (dateCtrl != null) {
                        updateRequest.resolvedDate = new Date(dateCtrl?.get("datePicker")?.value);
                    }
                }
                break;
            }               
            case IssueSteps.Escalate: {
                actionForm = <FormGroup>this.form.get("escalationForm");
                if (actionForm != null) {
                    const methodCtrl = actionForm.get("escalationMethod");
                    if (methodCtrl != null) {
                        const method = <IssueEscalationMethods>methodCtrl.value;
                        const escalation = {
                            escalationType: method
                        } as IssueEscalation;
                        let dateCtrl: FormGroup;
                        switch (method) {
                            case IssueEscalationMethods.ChiefsOrder:
                                escalation.currentChiefsOrderEscalationStatus = this.issue.escalationStatus;
                                escalation.chiefsOrderNumber = actionForm.get("chiefsOrderNumber").value;
                                dateCtrl = <FormGroup>actionForm.get("dateChiefsOrderIssued");
                                if (dateCtrl != null) {
                                    escalation.dateChiefsOrderIssued = new Date(dateCtrl?.get("datePicker")?.value);
                                }                                
                                if (updateRequest.statusChange == null) {
                                    if (!this.isApproveForm && this.issue.escalationStatus != "Chief's Order Approved") {
                                        status = IssuesStatus.ChiefsOrderApproval;
                                    }
                                    updateRequest.statusChange = { currentStatus: this.issue.status, newStatus: status } as IssueStatusChange
                                }
                                break;
                            case IssueEscalationMethods.ComplianceNotice:
                                escalation.complianceNoticeNumber = actionForm.get("complianceNoticeNumber").value;
                                dateCtrl = <FormGroup>actionForm.get("complianceNoticeIssued");
                                if (dateCtrl != null) {
                                    escalation.complianceNoticeIssued = new Date(dateCtrl.get("datePicker")?.value);
                                }
                                dateCtrl = <FormGroup>actionForm.get("complianceNoticeDueDate");
                                if (dateCtrl != null) {
                                    escalation.complianceNoticeDueDate = new Date(dateCtrl.get("datePicker")?.value);
                                }
                                break;
                            case IssueEscalationMethods.AttorneyGeneral:
                                escalation.referralNumber = actionForm.get("referralNumber").value;
                                dateCtrl = <FormGroup>actionForm.get("referralDate");
                                if (dateCtrl != null) {
                                    escalation.referralDate = new Date(dateCtrl.get("datePicker")?.value);
                                }
                                break;
                            case IssueEscalationMethods.ComplianceAgreement:
                                escalation.complianceAgreementNumber = actionForm.get("complianceAgreementNumber").value;
                                dateCtrl = <FormGroup>actionForm.get("complianceAgreementIssued");
                                if (dateCtrl != null) {
                                    escalation.complianceAgreementDate = new Date(dateCtrl.get("datePicker")?.value);
                                }
                                dateCtrl = <FormGroup>actionForm.get("complianceAgreementDueDate");
                                if (dateCtrl != null) {
                                    escalation.complianceAgreementDueDate = new Date(dateCtrl.get("datePicker")?.value);
                                }
                                break;
                        }
                        updateRequest.escalation = escalation;
                    }
                }
                break;
            }
            case IssueSteps.KeepWorking: {
                actionForm = <FormGroup>this.form.get("keepWorkingForm");
                if (actionForm != null) {
                    const extensionRequestedCtrl = actionForm.get("extensionRequested");
                    let extensionRequested: boolean = false;
                    if (extensionRequestedCtrl != null) {
                        extensionRequested = extensionRequestedCtrl.value == "Yes";
                    }
                    const extensionGrantedCtrl = actionForm.get("extensionGranted");
                    if (extensionGrantedCtrl != null) {
                        const granted = <boolean>extensionGrantedCtrl.value;
                        updateRequest.extensionDenied = extensionRequested && !granted;
                        if (granted) {
                            let previousDue = this.issue.dateDue;
                            if (previousDue == null) previousDue = new Date();
                            updateRequest.extension = {
                                issueId: this.issue.id,
                                extendedBy: this.auth.User.id,
                                extendedOn : new Date(),
                                previousDueDate: previousDue,
                                extensionDate: new Date(actionForm.get("extensionExpiration")?.get("datePicker")?.value)
                            } as IssueExtension;
                        }
                    }
                    const contestedCtrl = actionForm.get("contested");
                    if (contestedCtrl != null) {
                        const contested = contestedCtrl.value === "Yes";
                        if (contested) {
                            updateRequest.ownerContested = {
                                contested: contested,
                                contestedDate: new Date(actionForm.get("dateContested")?.get("datePicker")?.value),
                                contestReason: actionForm.get("contestedReason").get("content").value
                            } as ContestIssue;
                            if (updateRequest.statusChange == null) {
                                updateRequest.statusChange = { currentStatus: this.issue.status, newStatus: IssuesStatus.Contested } as IssueStatusChange;
                            } else {
                                updateRequest.statusChange.newStatus = IssuesStatus.Contested;
                            }
                        }
                    }
                }
                break;
            }
            default: {
                actionForm = <FormGroup>this.form.get("lettersForm");
                let notice: IssueNotice;
                switch (this.nextStepValue) {
                    case IssueSteps.PreLetter:
                        notice = {
                            issueId : this.issue.id,
                            noticeType: IssueNoticeType.PreLetter,
                            document: actionForm.get("preletter").value,
                            dateSent: new Date(actionForm.get("preletterMailed")?.get("datePicker")?.value),
                            dateReceived: new Date(actionForm.get("preletterReceived")?.get("datePicker")?.value)
                        } as IssueNotice;
                        if (this.preletter != null) notice.id = this.preletter.id;
                        break;
                    case IssueSteps.Letter1:
                        notice = {
                            issueId: this.issue.id,
                            noticeType: IssueNoticeType.Letter1,
                            document: actionForm.get("letter1").value,
                            dateSent: new Date(actionForm.get("letter1Mailed")?.get("datePicker")?.value),
                            dateReceived: new Date(actionForm.get("letter1Received")?.get("datePicker")?.value),
                            dateDue: new Date(actionForm.get("letter1DueDate")?.get("datePicker")?.value),
                            expirationDate: new Date(actionForm.get("letter1ExpirationDate")?.get("datePicker")?.value)
                        } as IssueNotice;
                        if (this.letter1 != null) notice.id = this.letter1.id;
                        break;
                    case IssueSteps.Letter2:
                        notice = {
                            issueId: this.issue.id,
                            noticeType: IssueNoticeType.Letter2,
                            document: actionForm.get("letter2").value,
                            dateSent: new Date(actionForm.get("letter2Mailed").get("datePicker").value),
                            dateReceived: new Date(actionForm.get("letter2Received").get("datePicker").value)
                        } as IssueNotice;
                        if (this.letter2 != null) notice.id = this.letter2.id;
                        break;
                    case IssueSteps.ComplianceNotice1:
                        notice = {
                            issueId: this.issue.id,
                            noticeType: IssueNoticeType.ComplianceNotice1,
                            document: actionForm.get('complianceNotice1').value,
                            dateSent: new Date(actionForm.get("complianceNotice1Mailed").get("datePicker").value),
                            dateReceived: new Date(actionForm.get('complianceNotice1Received').get("datePicker").value),
                            expirationDate: new Date(actionForm.get('complianceNotice1ExpirationDate').get("datePicker").value)
                        } as IssueNotice;
                        if (this.complianceNotice1 != null) notice.id = this.complianceNotice1.id;
                        break;
                    case IssueSteps.ComplianceNotice2:
                        notice = {
                            issueId: this.issue.id,
                            noticeType: IssueNoticeType.ComplianceNotice2,
                            document: actionForm.get("complianceNotice2").value,
                            dateSent: new Date(actionForm.get("complianceNotice2Mailed").get("datePicker").value),
                            dateReceived: new Date(actionForm.get('complianceNotice2Received').get('datePicker').value)
                        } as IssueNotice;
                        if (this.complianceNotice2 != null) notice.id = this.complianceNotice2.id;
                        break;
                }
                if (notice != null) {
                    updateRequest.notices.push(notice);
                }
                break;
            }
                
        }
        updateRequest.dateOfNextAction = new Date(this.form.get("dateOfNextAction").get("datePicker").value);
        if (this.assignedTo != null && this.assignedTo.id !== this.issue.assignee) {
            updateRequest.reassignment = {
                currentUser: this.issue.assignee,
                newUser: this.assignedTo.id
            } as IssueReassignment;
        }
        updateRequest.comments = this.issue.issueNotes;
        this.loader.toggleLoader(true);
        return this.issuesService.updateIssue(updateRequest)
    }

    showOrgNotFound(): void {
        this.organizationNotFoundDialog.message = this.organizationNotFoundMessage.replace("[XXX]", this.organizationName);
        this.organizationNotFoundDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.organizationNotFoundDialog);
    }


    viewSidePanel(): void {
        this.sidePanelService.newSidePanel(this.timelinePanel);    
        this.sidePanelService.toggleSidePanel(true);            
    }
}
